import {
  BooleanFilter,
  DevicesAnalysis_GetDevicesQueryVariables,
  MetricField,
} from '@/__generated__/graphql';
import { useDispatch, useSelector } from '@/store';
import { DistanceUnit, FlowUnit } from '@/utils/units';
import { skipToken, useSuspenseQuery } from '@apollo/client';
import {
  TransitionStartFunction,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useTransition,
} from 'react';
import DEVICES_ANALYSIS_GET_DEVICES, { Device, Gate } from '../graphql/queries/getDevices';
import DEVICES_ANALYSIS_GET_DEVICES_AVAILABLE_FIELDS from '../graphql/queries/getDevicesAvailableFields';
import {
  setAvailableFields,
  setData,
  TelemetryKey,
  toggleTelemetry,
} from '../slices/devicesAnalysisSlice';

export type DevicesAnalysisDevice = Device & {
  profile: Device['profile'] & {
    type: 'CFC';
  };
  metrics: Device['metrics'] & {
    upstreamLevel: NonNullable<Gate['upstreamDevice']>['metrics']['level'];
    downstreamLevel: NonNullable<Gate['downstreamDevice']>['metrics']['level'];
  };
  hasAlarm: Gate['hasAlarm'];
  alarmData: Gate['alarmData'];
};

interface DevicesAnalysisData {
  selectedDevice: DevicesAnalysisDevice | null;
  devices: DevicesAnalysisDevice[];
  handleRefetch: () => void;
  isPending: boolean;
  startTransition: TransitionStartFunction;
  showEventsTable: boolean;
}

export const useDevicesAnalysis = (): DevicesAnalysisData => {
  const dispatch = useDispatch();
  const [isPending, startTransition] = useTransition();
  const { parameters, telemetry, events } = useSelector((state) => state.devices_analysis_store);

  const ids = useMemo(() => {
    if (!parameters.deviceId) return null;
    return [
      parameters.deviceId,
      ...parameters.comparisonDeviceIds.filter((id) => id !== parameters.deviceId),
    ];
  }, [parameters.deviceId, parameters.comparisonDeviceIds]);

  const memoizedVariables: DevicesAnalysis_GetDevicesQueryVariables | null = useMemo(() => {
    if (!parameters.deviceId || !ids?.length) return null;
    if (!telemetry.availableFields || telemetry.availableFields.length === 0) return null;
    return {
      input: { ids, includeGates: true, deleted: BooleanFilter.All },
      gatesInput: { ids },
      metricsInput: {
        start: parameters.start,
        stop: parameters.stop,
        window: parameters.window,
      },
      fluxRange: {
        start: parameters.start,
        stop: parameters.stop,
      },
      flowUnit: telemetry.flowUnit,
      levelUnit: telemetry.levelUnit,
      connectionStateInput: {
        window: null,
      },
      includeFlow: telemetry.flow,
      includeAllocation: telemetry[TelemetryKey.Allocation],
      includeFlowRaw: telemetry.flowRaw,
      includeLevel: telemetry.level,
      includeLevelRaw: telemetry.levelRaw,
      includeVelocity: telemetry[TelemetryKey.Velocity],
      includeVelocityRaw: telemetry[TelemetryKey.VelocityRaw],
      includeDopplerVelocity: telemetry[TelemetryKey.DopplerVelocity],
      includeDopplerVelocityRaw: telemetry[TelemetryKey.DopplerVelocityRaw],
      includePosition: telemetry.position,
      includeUpstreamLevel: telemetry.upstreamLevel,
      includeDownstreamLevel: telemetry.downstreamLevel,
      includeAngle: telemetry[TelemetryKey.Angle],
      includeAngleNonCalibrated: telemetry[TelemetryKey.AngleNonCalibrated],
      includeCalculatedAngle: telemetry[TelemetryKey.CalculatedAngle],
      includeOriginalDistance: telemetry.originalDistance,
      includeSignalQuality: telemetry.signalQuality,
      includeConnectionState: telemetry.connectionState,
      includeAccumulatedVolume: telemetry.accumulatedVol,
      includeVolumeHour: telemetry.volumeHour,
      includeVolumeDay: telemetry.volumeDay,
      includeVolumeMonth: telemetry.volumeMonth,
      includeBatterySoc: telemetry.batterySoc,
      includeBatteryV: telemetry.batteryV,
      includeChargeI: telemetry.chargeI,
      includeChargeP: telemetry.chargeP,
      includeSolarI: telemetry.solarI,
      includeSolarP: telemetry.solarP,
      includeSolarV: telemetry.solarV,
      includeFlowAlerts: telemetry.flowAlerts,
      includeLevelAlerts: telemetry.levelAlerts,
      includeDistributionAlerts: telemetry.distributionAlerts,
      includeRatingCurvesLimits: telemetry.ratingCurvesLimits,
      includeTemperature: telemetry[TelemetryKey.Temperature],
      includeTemperatureRaw: telemetry[TelemetryKey.TemperatureRaw],
      includeConductivity: telemetry[TelemetryKey.Conductivity],
      includeConductivityRaw: telemetry[TelemetryKey.ConductivityRaw],
      includePh: telemetry[TelemetryKey.Ph],
      includePhRaw: telemetry[TelemetryKey.PhRaw],
      includeOrp: telemetry[TelemetryKey.Orp],
      includeOrpRaw: telemetry[TelemetryKey.OrpRaw],
      includeDissolvedOxygen: telemetry[TelemetryKey.DissolvedOxygen],
      includeDissolvedOxygenRaw: telemetry[TelemetryKey.DissolvedOxygenRaw],
      includeDissolvedOxygenSaturation: telemetry[TelemetryKey.DissolvedOxygenSaturation],
      includeDissolvedOxygenSaturationRaw: telemetry[TelemetryKey.DissolvedOxygenSaturationRaw],
      includeTurbidityNtu: telemetry[TelemetryKey.TurbidityNtu],
      includeTurbidityNtuRaw: telemetry[TelemetryKey.TurbidityNtuRaw],
      includeTurbidityFnu: telemetry[TelemetryKey.TurbidityFnu],
      includeTurbidityFnuRaw: telemetry[TelemetryKey.TurbidityFnuRaw],
      includeSnowLevel: telemetry[TelemetryKey.SnowLevel],
      includeSnowLevelRaw: telemetry[TelemetryKey.SnowLevelRaw],
      includeSnowTemperature: telemetry[TelemetryKey.SnowTemperature],
      includeSnowTemperatureRaw: telemetry[TelemetryKey.SnowTemperatureRaw],
      includeSnowRadiation: telemetry[TelemetryKey.SnowRadiation],
      includeSnowRadiationRaw: telemetry[TelemetryKey.SnowRadiationRaw],
      includeSnowWaterEquivalent: telemetry[TelemetryKey.SnowWaterEquivalent],
      includeSnowWaterEquivalentRaw: telemetry[TelemetryKey.SnowWaterEquivalentRaw],
      includeSnowWeight: telemetry[TelemetryKey.SnowWeight],
      includeSweNonCalibrated: telemetry[TelemetryKey.SweNonCalibrated],
      includeSnowLevelNonCalibrated: telemetry[TelemetryKey.SnowLevelNonCalibrated],
      includeEvents: events.show,
      includeStoredVolume: telemetry[TelemetryKey.StoredVolume],
    } satisfies DevicesAnalysis_GetDevicesQueryVariables;
  }, [parameters, telemetry, events, ids]);

  const { data: devicesAvailableFieldsData } = useSuspenseQuery(
    DEVICES_ANALYSIS_GET_DEVICES_AVAILABLE_FIELDS,
    ids?.length
      ? {
          variables: {
            input: {
              ids,
              includeGates: true,
              deleted: BooleanFilter.All,
            },
          },
        }
      : skipToken,
  );

  useEffect(() => {
    if (devicesAvailableFieldsData) {
      const devices = devicesAvailableFieldsData.devices;
      const availableFields = new Set<MetricField>();
      devices.forEach((device) => {
        device.metrics.availableFields.forEach((field) => {
          if (field) availableFields.add(field);
        });
      });
      dispatch(setAvailableFields([...availableFields]));
    }
  }, [devicesAvailableFieldsData, dispatch]);

  const { variables } = useDeferredValue({
    variables: memoizedVariables,
  });

  const { data, refetch } = useSuspenseQuery(
    DEVICES_ANALYSIS_GET_DEVICES,
    variables ? { variables, fetchPolicy: 'no-cache' } : skipToken,
  );

  useEffect(() => {
    dispatch(setData(data ?? null));
  }, [data, dispatch]);

  const handleRefetch = useCallback(() => {
    startTransition(() => {
      refetch();
    });
  }, [refetch]);

  // polling interval
  useEffect(() => {
    if (isNaN(parameters.pollingInterval)) return;
    const interval = setInterval(() => {
      handleRefetch();
    }, parameters.pollingInterval);
    return () => clearInterval(interval);
  }, [handleRefetch, data, parameters.pollingInterval]);

  const devices = useMemo(() => {
    const devices =
      data?.devices?.map((device) => {
        const isCFC = device.profile.type === 'CFC';
        if (isCFC) {
          const gate = data.gates?.find((gate) => gate.id === device.id);
          return {
            ...device,
            metrics: {
              ...device.metrics,
              upstreamLevel: gate?.upstreamDevice?.metrics?.level,
              downstreamLevel: gate?.downstreamDevice?.metrics?.level,
            },
            hasAlarm: gate?.hasAlarm,
            alarmData: gate?.alarmData,
          };
        }
        return device;
      }) ?? [];

    return devices as DevicesAnalysisDevice[];
  }, [data]);

  const selectedDevice = useMemo(() => {
    const device = devices.find((device) => device.id === parameters.deviceId);
    return device ?? null;
  }, [devices, parameters.deviceId]);

  // useEffect(() => {
  //   handleRefetch();
  // }, [handleRefetch, parameters.deviceId, parameters.window, parameters.stop, parameters.start]);

  useEffect(() => {
    if (!devicesAvailableFieldsData || devicesAvailableFieldsData.devices.length === 0) return;
    const devices = devicesAvailableFieldsData.devices;
    const selectedDevice = devices.find((device) => device.id === parameters.deviceId);
    if (!selectedDevice) return;
    const availableFields = selectedDevice.metrics.availableFields;
    const includeFlow = availableFields.includes(MetricField.Flow);
    const includeRatingCurvesLimits = availableFields.includes(MetricField.RatingCurvesLimits);
    const includeEnabledRatingCurves = availableFields.includes(MetricField.EnabledRatingCurves);
    if (
      (includeFlow && !includeRatingCurvesLimits) ||
      (includeRatingCurvesLimits && includeEnabledRatingCurves)
    )
      dispatch(
        toggleTelemetry({
          [TelemetryKey.Flow]: true,
        }),
      );
    else if (availableFields.includes(MetricField.Level))
      dispatch(
        toggleTelemetry({
          [TelemetryKey.Level]: true,
        }),
      );
    else if (availableFields.includes(MetricField.Position))
      dispatch(toggleTelemetry({ [TelemetryKey.Position]: true }));
    else if (availableFields.includes(MetricField.Temperature))
      dispatch(toggleTelemetry({ [TelemetryKey.Temperature]: true }));
    else if (availableFields.includes(MetricField.SnowLevel))
      dispatch(toggleTelemetry({ [TelemetryKey.SnowLevel]: true }));

    dispatch(
      toggleTelemetry({
        [TelemetryKey.FlowUnit]: selectedDevice.metrics.flow.unit as FlowUnit,
        [TelemetryKey.LevelUnit]: selectedDevice.metrics.level.unit as DistanceUnit,
      }),
    );

    if (availableFields.includes(MetricField.DgaFlow))
      dispatch(toggleTelemetry({ [TelemetryKey.DGAFlow]: true }));
    if (availableFields.includes(MetricField.DgaAccumulatedVolume))
      dispatch(toggleTelemetry({ [TelemetryKey.DGAAccumulatedVol]: true }));
    if (availableFields.includes(MetricField.DgaLevel))
      dispatch(toggleTelemetry({ [TelemetryKey.DGALevel]: true }));
  }, [devicesAvailableFieldsData, dispatch, parameters.deviceId]);

  return {
    selectedDevice,
    devices,
    handleRefetch,
    isPending,
    startTransition,
    showEventsTable: events.show,
  };
};
